import { useCallback, useState } from 'react';

export type EasterEggId = 'boss-signal' | 'turbo-attract' | 'fighter-armory';

interface PersistedEasterEggState {
  unlocked: EasterEggId[];
  armedFighterIds: string[];
  championRank?: number;
  playerName?: string;
}

const CABINET_ID_KEY = 'smackers-cabinet-id-v1';
const PROGRESS_KEY = 'smackers-easter-egg-progress-v1';

const emptyState = (): PersistedEasterEggState => ({ unlocked: [], armedFighterIds: [] });

function readProgress() {
  try {
    const parsed = JSON.parse(localStorage.getItem(PROGRESS_KEY) ?? 'null') as Partial<PersistedEasterEggState> | null;
    if (!parsed) return emptyState();
    return {
      unlocked: Array.isArray(parsed.unlocked)
        ? parsed.unlocked.filter((id): id is EasterEggId => id === 'boss-signal' || id === 'turbo-attract' || id === 'fighter-armory')
        : [],
      armedFighterIds: Array.isArray(parsed.armedFighterIds) ? parsed.armedFighterIds.filter((id): id is string => typeof id === 'string').slice(-2) : [],
      championRank: Number.isInteger(parsed.championRank) ? parsed.championRank : undefined,
      playerName: typeof parsed.playerName === 'string' ? parsed.playerName : undefined,
    };
  } catch {
    return emptyState();
  }
}

function writeProgress(state: PersistedEasterEggState) {
  try { localStorage.setItem(PROGRESS_KEY, JSON.stringify(state)); } catch { /* progress remains available for this page */ }
}

export function getCabinetId() {
  try {
    const saved = localStorage.getItem(CABINET_ID_KEY);
    if (saved) return saved;
    const created = `CAB-${crypto.randomUUID().slice(0, 8).toUpperCase()}`;
    localStorage.setItem(CABINET_ID_KEY, created);
    return created;
  } catch {
    return `CAB-${crypto.randomUUID().slice(0, 8).toUpperCase()}`;
  }
}

export function useEasterEggProgress() {
  const [state, setState] = useState(readProgress);

  const update = useCallback((change: (current: PersistedEasterEggState) => PersistedEasterEggState) => {
    setState((current) => {
      const next = change(current);
      writeProgress(next);
      return next;
    });
  }, []);

  const unlock = useCallback((id: EasterEggId) => update((current) => current.unlocked.includes(id)
    ? current
    : { ...current, unlocked: [...current.unlocked, id] }), [update]);

  const armFighter = useCallback((fighterId: string) => update((current) => ({
    ...current,
    unlocked: current.unlocked.includes('fighter-armory') ? current.unlocked : [...current.unlocked, 'fighter-armory'],
    armedFighterIds: [...current.armedFighterIds.filter((id) => id !== fighterId), fighterId].slice(-2),
  })), [update]);

  const consumeArmedFighters = useCallback(() => update((current) => ({ ...current, armedFighterIds: [] })), [update]);

  const rememberChampion = useCallback((rank: number, playerName: string) => update((current) => ({
    ...current,
    championRank: rank,
    playerName,
  })), [update]);

  return {
    ...state,
    allCoreEggsUnlocked: state.unlocked.length === 3,
    armFighter,
    consumeArmedFighters,
    rememberChampion,
    unlock,
  };
}
